/**
 * Consumable form (create / edit) — modal body wiring
 *
 * Loaded as a side-effect import from ``pages/index.js`` and called
 * by ``consumables.js`` from the dynamic-modal ``onContentInjected``
 * hook. Replaces the 161 LOC inline ``<script>`` that used to live
 * at the bottom of ``templates/office/stock/_consumable_form_content.html``.
 *
 * Two concerns, both scoped to the form root (no document-level
 * listeners — the form is short-lived and lives inside a modal):
 *
 *   1. Eager-clear validation : when the user types into a text /
 *      textarea / select that already carries a server-side
 *      ``.has-error`` class, drop the red border and the inline
 *      ``.text-red-600`` / ``.text-red-500`` message. Backend
 *      remains the single source of truth (hybrid pro pattern).
 *
 *   2. Single-slot image dropzone : the form has ONE preview slot
 *      that handles both the already-saved image (edit mode) and a
 *      newly-picked file. Click + drag-and-drop both pick into the
 *      same hidden ``<input type="file">``. The X button calls
 *      ``handleRemove()`` which writes ``remove_image=1`` only when
 *      the thing being removed was actually saved on the server.
 *      File validation (size + mime) happens client-side as a UX
 *      shortcut — the backend re-validates regardless.
 *
 * No globals are exposed: ``consumables.js`` calls
 * ``initConsumableForm(content)`` directly and gets the i18n strings
 * from a ``{{ … |json_script:"consumable-form-config" }}`` block
 * rendered alongside the form body.
 *
 * Picked images are uploaded as-is (after a client-side size + mime
 * check as a UX shortcut). Optimisation — EXIF auto-rotate, metadata
 * strip, downscale and WebP re-encode (full + thumbnail) — happens
 * server-side in ``apps.core.image_utils.build_consumable_image_webp``,
 * which preserves transparency (a client-side JPEG pass would flatten
 * it) and produces the thumbnail the catalogue / list grids use.
 */
function readJsonScript(id, fallback) {
  const node = document.getElementById(id);
  if (!node) return fallback;
  try {
    return JSON.parse(node.textContent);
  } catch (e) { // eslint-disable-line no-unused-vars
    return fallback;
  }
}

// ────────────────────────────────────────────────────────────────────
// 1) Eager-clear validation (delegated on the form root)
// ────────────────────────────────────────────────────────────────────

function clearFieldError(field) {
  if (!field) return;
  const fieldGroup = field.closest('.field-group');
  if (!fieldGroup) return;
  fieldGroup.classList.remove('has-error');
  field.classList.remove('border-red-500');
  fieldGroup
    .querySelectorAll('p.text-red-600, p.text-red-500')
    .forEach((msg) => msg.remove());
}

function bindEagerClear(form) {
  if (form._cfEagerBound) return;
  form._cfEagerBound = true;

  form.addEventListener('input', (e) => {
    const field = e.target;
    if (
      (field.tagName === 'INPUT' || field.tagName === 'TEXTAREA') &&
      field.value.trim()
    ) {
      clearFieldError(field);
    }
  });

  form.addEventListener('change', (e) => {
    const field = e.target;
    if (field.tagName === 'SELECT' && field.value) {
      clearFieldError(field);
    }
    if (field.tagName === 'INPUT' && field.value.trim()) {
      clearFieldError(field);
    }
  });
}

// ────────────────────────────────────────────────────────────────────
// 2) Image dropzone
// ────────────────────────────────────────────────────────────────────

function formatFileSize(bytes) {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

function bindDropzone(content, i18n) {
  const dropzone = content.querySelector('#image-dropzone');
  const fileInput = content.querySelector('#id_image');
  const emptyEl = content.querySelector('#image-empty');
  const previewEl = content.querySelector('#image-preview');
  const previewImage = content.querySelector('#preview-image');
  const previewFilename = content.querySelector('#preview-filename');
  const previewFilesize = content.querySelector('#preview-filesize');
  const removeBtn = content.querySelector('#image-remove');
  const removeImageInput = content.querySelector('#remove_image');

  if (!dropzone || !fileInput) return;
  if (dropzone._cfDzBound) return;
  dropzone._cfDzBound = true;

  const MAX_SIZE = 2 * 1024 * 1024;

  // Track whether the page rendered with a saved image. Clicking X on a
  // saved image must tell the server to delete it (remove_image=1). For
  // a not-yet-saved pending upload, clearing is purely client-side.
  let hadSavedImage = previewImage && previewImage.getAttribute('src') !== '';

  const invalidTypeMsg =
    i18n.invalidFileType ||
    'Invalid file type. Please upload JPG, PNG, GIF, or WebP.';
  const tooLargeMsg = i18n.fileTooLarge || 'File too large. Maximum size is 2MB.';

  // Client-side guard only (UX shortcut). The backend re-validates and
  // re-encodes the image to WebP regardless.
  function validateFile(file) {
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      window.alert(invalidTypeMsg);
      return false;
    }
    if (file.size > MAX_SIZE) {
      window.alert(tooLargeMsg);
      return false;
    }
    return true;
  }

  function showEmpty() {
    emptyEl.classList.remove('hidden');
    previewEl.classList.add('hidden');
    removeBtn.classList.add('hidden');
    previewImage.src = '';
    previewFilename.textContent = '';
    previewFilesize.textContent = '';
  }

  function showPreview(src, title, meta) {
    previewImage.src = src;
    previewFilename.textContent = title || '';
    previewFilesize.textContent = meta || '';
    emptyEl.classList.add('hidden');
    previewEl.classList.remove('hidden');
    removeBtn.classList.remove('hidden');
    dropzone.classList.remove('border-red-500');
  }

  function handleFile(file) {
    if (!validateFile(file)) {
      fileInput.value = '';
      return;
    }
    const reader = new window.FileReader();
    reader.onload = function (e) {
      removeImageInput.value = '';
      showPreview(e.target.result, file.name, formatFileSize(file.size));
    };
    reader.readAsDataURL(file);
  }

  function handleRemove() {
    fileInput.value = '';
    if (hadSavedImage) {
      removeImageInput.value = '1';
      hadSavedImage = false;
    }
    showEmpty();
  }

  dropzone.addEventListener('click', (e) => {
    if (e.target === removeBtn || removeBtn.contains(e.target)) return;
    fileInput.click();
  });

  fileInput.addEventListener('change', function () {
    if (this.files && this.files[0]) handleFile(this.files[0]);
  });

  removeBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    handleRemove();
  });

  dropzone.addEventListener('dragover', (e) => {
    e.preventDefault();
    dropzone.classList.add(
      'border-primary-500',
      'bg-primary-50',
      'dark:bg-primary-900/20',
    );
  });
  dropzone.addEventListener('dragleave', (e) => {
    e.preventDefault();
    dropzone.classList.remove(
      'border-primary-500',
      'bg-primary-50',
      'dark:bg-primary-900/20',
    );
  });
  dropzone.addEventListener('drop', (e) => {
    e.preventDefault();
    dropzone.classList.remove(
      'border-primary-500',
      'bg-primary-50',
      'dark:bg-primary-900/20',
    );
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const dt = new window.DataTransfer();
      dt.items.add(e.dataTransfer.files[0]);
      fileInput.files = dt.files;
      handleFile(e.dataTransfer.files[0]);
    }
  });
}

// ────────────────────────────────────────────────────────────────────
// MAIN INIT
// ────────────────────────────────────────────────────────────────────

/**
 * Call this from the dynamic-modal ``onContentInjected`` hook with
 * the modal's freshly-injected content node. Idempotent — calling
 * twice on the same form is a no-op (per-node ``_cfEagerBound`` /
 * ``_cfDzBound`` flags).
 *
 * @param {Element} content - The modal-content element holding the form.
 */
export function initConsumableForm(content) {
  if (!content) return;
  const form = content.querySelector('#consumable-form');
  if (!form) return; // not the consumable modal — no-op

  const config = readJsonScript('consumable-form-config', {});
  const i18n = config.i18n || {};

  bindEagerClear(form);
  bindDropzone(content, i18n);
}
